import { useState } from "react";
import {
  Building2,
  Users,
  DollarSign,
  Calendar,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  UserCheck,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const departmentIcons = {
  "Ressources Humaines": { icon: UserCheck, color: "text-blue-600" },
  Informatique: { icon: Building2, color: "text-purple-600" },
  Marketing: { icon: TrendingUp, color: "text-green-600" },
  Commercial: { icon: DollarSign, color: "text-orange-600" },
  Production: { icon: Building2, color: "text-gray-600" },
  default: { icon: Building2, color: "text-blue-600" },
};

export default function DepartmentTable({
  departments = [],
  onView,
  onEdit,
  onDelete,
  onViewEmployees,
  sortConfig = { field: null, direction: "asc" },
  onSort,
}) {
  const [hoveredRow, setHoveredRow] = useState(null);

  const formatCurrency = (amount) => {
    if (!amount) return "Non défini";
    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount)) return "Non défini";
    return new Intl.NumberFormat("fr-MA", {
      style: "currency",
      currency: "MAD",
      notation: numericAmount > 1000000 ? "compact" : "standard",
    }).format(numericAmount);
  };

  const formatDate = (date) => {
    if (!date) return "Non défini";
    return new Intl.DateTimeFormat("fr-FR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(date));
  };

  const getDepartmentIcon = (name) => {
    const config = departmentIcons[name] || departmentIcons.default;
    return config;
  };

  const getSortIcon = (field) => {
    if (sortConfig.field !== field) {
      return <ArrowUpDown className="h-4 w-4 opacity-50" />;
    }
    return sortConfig.direction === "asc" ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  const handleSort = (field) => {
    if (onSort) {
      const direction =
        sortConfig.field === field && sortConfig.direction === "asc"
          ? "desc"
          : "asc";
      onSort({ field, direction });
    }
  };

  const TableHeaderCell = ({ field, children, className }) => (
    <TableHead
      className={cn(
        "cursor-pointer select-none hover:bg-gray-50 transition-colors",
        className
      )}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center justify-between group">
        {children}
        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
          {getSortIcon(field)}
        </div>
      </div>
    </TableHead>
  );

  if (departments.length === 0) {
    return (
      <div className="text-center py-12">
        <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500 text-lg">Aucun département trouvé</p>
        <p className="text-gray-400 text-sm">
          Ajustez vos filtres ou créez un nouveau département
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50/50">
            <TableHeaderCell field="nom" className="w-1/4">
              Département
            </TableHeaderCell>
            <TableHeaderCell field="manager" className="w-1/5">
              Manager
            </TableHeaderCell>
            <TableHeaderCell field="_count.employes" className="w-1/8">
              Employés
            </TableHeaderCell>
            <TableHeaderCell field="budget" className="w-1/6">
              Budget
            </TableHeaderCell>
            <TableHeaderCell field="actif" className="w-1/8">
              Statut
            </TableHeaderCell>
            <TableHeaderCell field="date_creation" className="w-1/6">
              Date création
            </TableHeaderCell>
            <TableHead className="w-16">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {departments.map((department) => {
            const iconConfig = getDepartmentIcon(department.nom);
            const IconComponent = iconConfig.icon;
            const budgetUtilization =
              department.budget && department._count?.employes
                ? Math.min(
                    ((department._count.employes * 50000) /
                      parseFloat(department.budget)) *
                      100,
                    100
                  )
                : 0;

            return (
              <TableRow
                key={department.id}
                className={cn(
                  "transition-colors duration-150 hover:bg-gray-50/50",
                  hoveredRow === department.id && "bg-blue-50/30"
                )}
                onMouseEnter={() => setHoveredRow(department.id)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                {/* Department Name & Description */}
                <TableCell className="font-medium">
                  <div className="flex items-center space-x-3">
                    <div className="p-1.5 rounded-md bg-gray-100">
                      <IconComponent
                        className={cn("h-4 w-4", iconConfig.color)}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900 truncate">
                        {department.nom}
                      </div>
                      {department.description && (
                        <div className="text-sm text-gray-500 truncate max-w-48">
                          {department.description}
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>

                {/* Manager */}
                <TableCell>
                  {department.manager ? (
                    <div className="flex items-center space-x-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs bg-blue-100 text-blue-600">
                          {department.manager.prenom?.[0]}
                          {department.manager.nom?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm truncate max-w-32">
                        {department.manager.prenom} {department.manager.nom}
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400 italic">
                      Non assigné
                    </span>
                  )}
                </TableCell>

                {/* Employee Count */}
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewEmployees(department)}
                    className="h-auto p-1 hover:bg-blue-50"
                  >
                    <Badge
                      variant="secondary"
                      className="bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors cursor-pointer"
                    >
                      <Users className="h-3 w-3 mr-1" />
                      {department._count?.employes || 0}
                    </Badge>
                  </Button>
                </TableCell>

                {/* Budget */}
                <TableCell>
                  <div className="space-y-1">
                    <div className="text-sm font-medium">
                      {formatCurrency(department.budget)}
                    </div>
                    {department.budget && budgetUtilization > 0 && (
                      <div className="flex items-center space-x-2">
                        <div className="w-16 bg-gray-200 rounded-full h-1">
                          <div
                            className={cn(
                              "h-1 rounded-full transition-all duration-300",
                              budgetUtilization > 80
                                ? "bg-red-500"
                                : budgetUtilization > 60
                                ? "bg-yellow-500"
                                : "bg-green-500"
                            )}
                            style={{ width: `${budgetUtilization}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">
                          {budgetUtilization.toFixed(0)}%
                        </span>
                      </div>
                    )}
                  </div>
                </TableCell>

                {/* Status */}
                <TableCell>
                  <Badge
                    variant={department.actif ? "default" : "secondary"}
                    className={cn(
                      "transition-all duration-200",
                      department.actif
                        ? "bg-green-100 text-green-800 hover:bg-green-200"
                        : "bg-gray-100 text-gray-600"
                    )}
                  >
                    {department.actif ? "Actif" : "Inactif"}
                  </Badge>
                </TableCell>

                {/* Creation Date */}
                <TableCell>
                  <div className="text-sm text-gray-600">
                    {formatDate(department.date_creation)}
                  </div>
                </TableCell>

                {/* Actions */}
                <TableCell>
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onView(department)}
                      className={cn(
                        "h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity",
                        hoveredRow === department.id && "opacity-100"
                      )}
                      title="Voir détails"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => onView(department)}>
                          <Eye className="h-4 w-4 mr-2" />
                          Voir détails
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onViewEmployees(department)}
                        >
                          <Users className="h-4 w-4 mr-2" />
                          Voir employés
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onEdit(department)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Modifier
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onDelete(department, false)}
                          className="text-red-600 focus:text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {department.actif ? "Désactiver" : "Supprimer"}
                        </DropdownMenuItem>
                        {!department.actif && (
                          <DropdownMenuItem
                            onClick={() => onDelete(department, true)}
                            className="text-red-800 focus:text-red-800"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Supprimer définitivement
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
